import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { useDialog } from '../components/Dialog';
import api, { uploadAPI } from '../utils/api';
import {
  requestNotificationPermission,
  subscribeToPushNotifications,
  getNotificationPermission,
  showLocalNotification,
} from '../utils/notifications';
import { User, Mail, Phone, Building2, Calendar, LogOut, Lock, Globe, Bell, Camera } from 'lucide-react';
import './ProfilePage.css';
import { imageUrl } from '../utils/format';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user: authUser, logout, language, changeLanguage, refreshUser } = useAuth();
  const { t } = useTranslation(language);
  const { showError, showSuccess, showConfirm, DialogComponent } = useDialog();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(authUser);
  const [notificationEnabled, setNotificationEnabled] = useState(
    getNotificationPermission() === 'granted'
  );
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/ctv/profile');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setUser(authUser);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    showConfirm(
      t('logoutConfirm') || (language === 'vi' ? 'Bạn có chắc muốn đăng xuất?' : language === 'ko' ? '로그아웃하시겠습니까?' : 'Are you sure you want to logout?'),
      async () => {
        await logout();
      }
    );
  };

  const handleChangePassword = () => {
    showError(
      language === 'vi' ? 'Thông báo' : language === 'ko' ? '알림' : 'Notice',
      language === 'vi' ? 'Chức năng đổi mật khẩu đang được phát triển' : language === 'ko' ? '비밀번호 변경 기능은 개발 중입니다' : 'Password change feature is under development'
    );
  };

  const handleNotificationToggle = async () => {
    if (!notificationEnabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotificationEnabled(true);
        await subscribeToPushNotifications();
        showLocalNotification(t('notificationEnabled'), {
          body: t('notificationEnabledBody'),
        });
      }
    }
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setShowLanguageModal(false);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError(
        language === 'vi' ? 'Lỗi' : language === 'ko' ? '오류' : 'Error',
        t('pleaseSelectImageFile')
      );
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError(
        language === 'vi' ? 'Lỗi' : language === 'ko' ? '오류' : 'Error',
        language === 'vi' ? 'Kích thước file tối đa 5MB' : language === 'ko' ? '파일 크기는 최대 5MB입니다' : 'Max file size is 5MB'
      );
      return;
    }

    setUploadingAvatar(true);

    try {
      // Upload file
      const uploadResponse = await uploadAPI.uploadFile(file);
      const avatarUrl = uploadResponse.data?.url || uploadResponse.data?.path;

      if (!avatarUrl) {
        throw new Error('Upload failed');
      }

      // Update user avatar
      await api.put('/ctv/profile/avatar', { avatarUrl });

      // Refresh user data
      await refreshUser();
      await fetchProfile(); // Refresh profile data

      showSuccess(
        t('updateSuccessful'),
        t('avatarUpdated')
      );
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      showError(
        language === 'vi' ? 'Lỗi' : language === 'ko' ? '오류' : 'Error',
        language === 'vi' ? 'Không thể cập nhật ảnh đại diện' : language === 'ko' ? '프로필 사진을 업데이트할 수 없습니다' : 'Cannot update avatar'
      );
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const languages = [
    { code: 'vi', label: '🇻🇳 Tiếng Việt', shortLabel: 'Tiếng Việt' },
    { code: 'en', label: '🇬🇧 English', shortLabel: 'English' },
    { code: 'ko', label: '🇰🇷 한국어', shortLabel: '한국어' },
  ];

  const profileSections = [
    {
      title: t('accountInfo'),
      items: [
        {
          icon: User,
          label: t('fullName'),
          value: user?.name || 'N/A',
        },
        {
          icon: Mail,
          label: t('email'),
          value: user?.email || 'N/A',
        },
        {
          icon: Phone,
          label: t('phone'),
          value: user?.phone || 'N/A',
        },
        {
          icon: Building2,
          label: t('userType'),
          value: user?.userType === 201 ? 'CTV' : user?.userType === 101 ? 'Admin' : 'User',
        },
        {
          icon: Calendar,
          label: t('joinDate'),
          value: user?.createdAt
            ? new Date(user.createdAt).toLocaleDateString(
                language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US'
              )
            : 'N/A',
        },
      ],
    },
  ];

  const settingsItems = [
    {
      icon: Bell,
      label: t('notifications'),
      type: 'toggle',
      value: notificationEnabled,
      onChange: handleNotificationToggle,
    },
    {
      icon: Globe,
      label: t('language'),
      type: 'button',
      value: languages.find(l => l.code === language)?.shortLabel || 'Tiếng Việt',
      onClick: () => setShowLanguageModal(true),
    },
    {
      icon: Lock,
      label: t('changePassword'),
      type: 'button',
      onClick: handleChangePassword,
    },
  ];

  const actionItems = [
    {
      icon: LogOut,
      label: t('logout'),
      onClick: handleLogout,
      color: 'danger',
    },
  ];

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          <div className="profile-avatar" onClick={handleAvatarClick} style={{ cursor: 'pointer', position: 'relative' }}>
            {user?.image ? (
              <img src={(user.image)} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <User size={48} />
            )}
            {uploadingAvatar && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                <div className="spinner-sm"></div>
              </div>
            )}
          </div>
          <button
            className="avatar-upload-btn"
            onClick={handleAvatarClick}
            disabled={uploadingAvatar}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--primary-color)',
              border: '3px solid var(--surface-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            <Camera size={20} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
        <h1 className="profile-name">{user?.name || 'User'}</h1>
        <p className="profile-role">
          {user?.userType === 201 ? t('collaborator') : user?.userType === 101 ? t('administrator') : t('user')}
        </p>
      </div>

      {/* Profile Sections */}
      <div className="profile-content">
        {profileSections.map((section, index) => (
          <div key={index} className="profile-section">
            <h2 className="section-title">{section.title}</h2>
            <div className="info-list">
              {section.items.map((item, idx) => (
                <div key={idx} className="info-item">
                  <div className="info-icon">
                    <item.icon size={20} />
                  </div>
                  <div className="info-content">
                    <div className="info-label">{item.label}</div>
                    <div className="info-value">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Settings Items */}
        <div className="profile-section">
          <h2 className="section-title">{t('settings')}</h2>
          <div className="settings-list">
            {settingsItems.map((item, idx) => (
              item.type === 'toggle' ? (
                <div key={idx} className="setting-item" onClick={item.onChange}>
                  <div className="setting-left">
                    <div className="setting-icon">
                      <item.icon size={20} />
                    </div>
                    <div className="setting-content">
                      <div className="setting-label">{item.label}</div>
                    </div>
                  </div>
                  <div className="setting-right">
                    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={item.value}
                        onChange={item.onChange}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              ) : (
                <button key={idx} className="setting-item setting-item-clickable" onClick={item.onClick}>
                  <div className="setting-left">
                    <div className="setting-icon">
                      <item.icon size={20} />
                    </div>
                    <div className="setting-content">
                      <div className="setting-label">{item.label}</div>
                      {item.value && (
                        <div className="setting-value">{item.value}</div>
                      )}
                    </div>
                  </div>
                  <div className="setting-right">
                    <span className="setting-arrow">›</span>
                  </div>
                </button>
              )
            ))}
          </div>
        </div>

        {/* Action Items */}
        <div className="profile-section">
          <div className="action-list">
            {actionItems.map((item, idx) => (
              <button
                key={idx}
                className={`action-item ${item.color}`}
                onClick={item.onClick}
              >
                <div className="action-icon">
                  <item.icon size={20} />
                </div>
                <div className="action-content">
                  <div className="action-label">{item.label}</div>
                  {item.value && <div className="action-value">{item.value}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="modal-overlay" onClick={() => setShowLanguageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('selectLanguage')}</h3>
              <button className="modal-close" onClick={() => setShowLanguageModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-option ${language === lang.code ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className="language-label">{lang.label}</span>
                  {language === lang.code && <span className="check-icon">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dialog Component */}
      <DialogComponent />
    </div>
  );
};

export default ProfilePage;
